'use client'

import { useEffect } from 'react'
import { useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { useSearchStore } from '@/lib/store/search-store'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { DEFAULT_SEARCH_CATEGORY } from '@/lib/config'
import { getSearchLink } from '@/lib/utils'
import { decodeSearchTerm } from '@/lib/search/search-navigation'
import { startAssetsGridTransition } from '@/lib/hooks/use-assets-grid-transition'

export default function SearchStoreRouter() {
  const appEvent$ = useEventEmitterContext()
  const search = useSearchStore((state) => state.search)
  const setSearch = useSearchStore((state) => state.setSearch)
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)
  const setSearchMainCategorySlug = useSearchStore((state) => state.setSearchMainCategorySlug)
  const router = useRouter()
  const pathname = usePathname()
  const isSearchPage = pathname.startsWith('/search')
  const { params } = useParams()
  const searchParams = useSearchParams()
  const mainCategoryQueryParam = searchParams.get('main_category')

  useEffect(() => {
    console.debug('SearchStoreRouter mounted')
  }, [])

  // Resolve the current effective mainSearchCategorySlug
  const currentMainCategory = searchMainCategorySlug ?? DEFAULT_SEARCH_CATEGORY

  // Subscribe to the app event emitter for search callbacks
  appEvent$.useSubscription((evt) => {
    if (evt.type === 'search:callback') {
      const { query, mainCategory } = evt.payload
      let searchStateUpdated = false
      console.debug('search:callback event received', { query, mainCategory })

      if (query !== (search ?? '')) {
        console.debug('Updating search query in store', search, '>', query)
        setSearch(query)
        searchStateUpdated = true
      }

      if (mainCategory !== currentMainCategory) {
        console.debug('Updating search main category in store', currentMainCategory, '>', mainCategory)
        setSearchMainCategorySlug(mainCategory)
        searchStateUpdated = true
      }

      if (!searchStateUpdated) {
        console.debug('No changes in search state, skipping navigation')
        return
      }

      const newPathname = getSearchLink(query ?? '', mainCategory)
      // The header normalizes legacy plus-separated URLs during initialization.
      const isCurrentSearch =
        isSearchPage &&
        (decodeSearchTerm(params?.[0]) ?? '').replace(/\+/g, ' ') === (query ?? '') &&
        (mainCategoryQueryParam ?? DEFAULT_SEARCH_CATEGORY) === mainCategory

      if (!isCurrentSearch) startAssetsGridTransition(newPathname)
      router.push(newPathname)
    }
  })

  // Also when url changes, update the search state
  useEffect(() => {
    if (isSearchPage) {
      const [rawQuery] = params ?? []
      const query = decodeSearchTerm(rawQuery)

      console.debug('On search page, syncing search state with URL', { query, params, mainCategoryQueryParam })
      setSearch(query || undefined)

      const resolvedMainCategory = mainCategoryQueryParam ?? DEFAULT_SEARCH_CATEGORY
      setSearchMainCategorySlug(resolvedMainCategory)

      // Ensure the main_category query param is set in the URL
      if (typeof window !== 'undefined') {
        if (!mainCategoryQueryParam) {
          const parameters = new URLSearchParams(searchParams.toString())
          parameters.set('main_category', resolvedMainCategory)
          const newPathname = `${getSearchLink(query ?? '')}?${parameters}`
          router.replace(newPathname)
        }
      }
    }
  }, [isSearchPage, mainCategoryQueryParam, params, router, searchParams, setSearch, setSearchMainCategorySlug])

  // Clear the search state when navigating away from the search page
  useEffect(() => {
    if (!isSearchPage) {
      console.debug('Not on search page, clearing search state')
      setSearch(undefined)
      setSearchMainCategorySlug(undefined)
    }
  }, [isSearchPage, setSearch, setSearchMainCategorySlug])

  return null
}
